import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';

const MedicationRiskAlerts = ({ risks = [] }) => {
  if (!risks || risks.length === 0) {
    return (
      <Card className="border-2 border-green-200 bg-green-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <h4 className="font-semibold text-green-900 mb-1">Your Routine Looks Good</h4>
              <p className="text-sm text-green-800">
                You're managing your medications well. Keep up the great work!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSeverityConfig = (severity) => {
    const configs = {
      high: {
        bgClass: 'bg-purple-100 border-purple-300',
        textClass: 'text-purple-900',
        badgeClass: 'bg-purple-500 text-white',
        icon: Info,
        label: 'Let\'s Focus Here'
      },
      medium: {
        bgClass: 'bg-blue-100 border-blue-300',
        textClass: 'text-blue-900',
        badgeClass: 'bg-blue-500 text-white',
        icon: Info,
        label: 'Worth Noting'
      },
      low: {
        bgClass: 'bg-green-100 border-green-300',
        textClass: 'text-green-900',
        badgeClass: 'bg-green-500 text-white',
        icon: Info,
        label: 'Keep This in Mind'
      }
    };
    return configs[severity] || configs.medium;
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === 'high') {
      return <Badge className="bg-purple-500 text-white text-xs">We're Here to Help</Badge>;
    } else if (urgency === 'medium') {
      return <Badge className="bg-blue-500 text-white text-xs">Gentle Reminder</Badge>;
    }
    return <Badge className="bg-green-500 text-white text-xs">Tip</Badge>;
  };

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center text-purple-900">
          <Info className="h-5 w-5 mr-2" />
          Medication Insights ({risks.length})
        </CardTitle>
        <p className="text-xs text-purple-700 mt-1">
          Small improvements can make a big difference
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {risks.map((risk, index) => {
            const config = getSeverityConfig(risk.severity);
            const Icon = config.icon;

            return (
              <Alert key={index} className={`${config.bgClass} border-2`}>
                <div className="flex items-start gap-3">
                  <Icon className={`h-6 w-6 ${config.textClass} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h4 className={`font-bold text-sm ${config.textClass}`}>
                        {risk.title}
                      </h4>
                      <Badge className={config.badgeClass}>
                        {config.label}
                      </Badge>
                      {getUrgencyBadge(risk.urgency)}
                    </div>
                    
                    <AlertDescription className={`text-sm ${config.textClass} mb-2`}>
                      {risk.message}
                    </AlertDescription>
                    
                    {risk.recommendation && (
                      <div className={`mt-2 p-2 bg-white/70 rounded border ${config.textClass.replace('900', '700')}`}>
                        <p className="text-xs font-semibold mb-1">💡 How to Improve:</p>
                        <p className="text-xs">{risk.recommendation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Alert>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicationRiskAlerts;
